import {
  ArrowBack as BackIcon,
  Error as ErrorIcon,
  Payment as PaymentIcon,
  CheckCircle as SuccessIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { format } from "date-fns-jalali";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchInvoiceDetails,
  initiatePayment,
  verifyPayment,
} from "../../../application/actions/financialActions";
import {
  selectSelectedInvoiceData,
  selectSelectedInvoiceLoading,
} from "../../../application/selectors/financial";

const PaymentGateway = () => {
  const { invoiceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux State
  const invoice = useSelector(selectSelectedInvoiceData);
  const loading = useSelector(selectSelectedInvoiceLoading);

  // Local State
  const [activeStep, setActiveStep] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Online");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const steps = ["تأیید اطلاعات", "پرداخت", "نتیجه"];

  useEffect(() => {
    if (invoiceId) {
      dispatch(fetchInvoiceDetails(invoiceId));
    }
  }, [invoiceId, dispatch]);

  // Check for payment callback (authority and status in query params)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const authority = searchParams.get("Authority");
    const status = searchParams.get("Status");

    if (authority && status) {
      handlePaymentCallback(authority, status);
    }
  }, [location.search]);

  const handlePaymentCallback = async (authority, status) => {
    if (status === "OK") {
      setVerifying(true);
      setActiveStep(2);

      try {
        const result = await dispatch(
          verifyPayment({
            authority,
            invoiceId,
          })
        ).unwrap();

        setPaymentResult({
          success: true,
          referenceNumber: result.referenceNumber,
          message: "پرداخت با موفقیت انجام شد",
        });

        toast.success("پرداخت با موفقیت انجام شد");
      } catch (error) {
        setPaymentResult({
          success: false,
          message: error.message || "خطا در تأیید پرداخت",
        });
        toast.error("خطا در تأیید پرداخت");
      } finally {
        setVerifying(false);
      }
    } else {
      setActiveStep(2);
      setPaymentResult({
        success: false,
        message: "پرداخت لغو شد یا با خطا مواجه شد",
      });
      toast.error("پرداخت ناموفق بود");
    }
  };

  const handleInitiatePayment = async () => {
    if (!acceptTerms) {
      toast.error("لطفاً شرایط و قوانین را مطالعه و تأیید کنید");
      return;
    }

    setProcessing(true);

    try {
      const result = await dispatch(
        initiatePayment({
          invoiceId,
          amount: invoice.totalAmount,
          paymentMethod,
          callbackUrl: `${window.location.origin}/payment/${invoiceId}`,
        })
      ).unwrap();

      if (result.redirectUrl) {
        // Redirect to payment gateway
        window.location.href = result.redirectUrl;
      } else {
        toast.error("خطا در اتصال به درگاه پرداخت");
      }
    } catch (error) {
      toast.error(error.message || "خطا در ایجاد درخواست پرداخت");
      setProcessing(false);
    }
  };

  const handleBackToInvoice = () => {
    navigate(`/invoices`);
  };

  const handleBackToOrder = () => {
    if (invoice?.orderId) {
      navigate(`/orders/${invoice.orderId}`);
    }
  };

  const handleNextStep = () => {
    if (activeStep === 0) {
      setActiveStep(1);
    } else if (activeStep === 1) {
      handleInitiatePayment();
    }
  };

  const handleBackStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">فاکتور یافت نشد</Alert>
      </Container>
    );
  }

  if (invoice.status === "Paid") {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="info">این فاکتور قبلاً پرداخت شده است</Alert>
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={handleBackToInvoice}>
            بازگشت به لیست فاکتورها
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBackToInvoice}
          sx={{ mb: 2 }}
        >
          بازگشت به فاکتورها
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          درگاه پرداخت
        </Typography>
        <Typography variant="body1" color="text.secondary">
          فاکتور شماره: {invoice.invoiceNumber}
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step 0: Invoice Details */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            اطلاعات فاکتور
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                شماره فاکتور:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {invoice.invoiceNumber}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                تاریخ صدور:
              </Typography>
              <Typography variant="body1">
                {format(new Date(invoice.issueDate), "yyyy/MM/dd")}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                سررسید:
              </Typography>
              <Typography variant="body1">
                {format(new Date(invoice.dueDate), "yyyy/MM/dd")}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                شماره سفارش:
              </Typography>
              <Typography variant="body1">
                {invoice.orderNumber || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  bgcolor: "primary.light",
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  مبلغ قابل پرداخت:
                </Typography>
                <Typography
                  variant="h4"
                  color="primary"
                  sx={{ fontWeight: "bold" }}
                >
                  {invoice.totalAmount.toLocaleString("fa-IR")} ریال
                </Typography>
              </Box>
            </Grid>

            {invoice.description && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  توضیحات:
                </Typography>
                <Typography variant="body1">{invoice.description}</Typography>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
            <Button onClick={handleBackToInvoice}>انصراف</Button>
            <Button variant="contained" onClick={handleNextStep}>
              ادامه
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 1: Payment Method */}
      {activeStep === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            انتخاب روش پرداخت
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Card
            sx={{
              mb: 3,
              border: paymentMethod === "Online" ? 2 : 1,
              borderColor:
                paymentMethod === "Online" ? "primary.main" : "divider",
              cursor: "pointer",
            }}
            onClick={() => setPaymentMethod("Online")}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PaymentIcon
                  sx={{ fontSize: 40, mr: 2, color: "primary.main" }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6">پرداخت آنلاین</Typography>
                  <Typography variant="body2" color="text.secondary">
                    پرداخت از طریق درگاه بانکی
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Box
            sx={{
              bgcolor: "grey.100",
              p: 2,
              borderRadius: 1,
              mb: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              مبلغ قابل پرداخت:
            </Typography>
            <Typography
              variant="h5"
              color="primary"
              sx={{ fontWeight: "bold" }}
            >
              {invoice.totalAmount.toLocaleString("fa-IR")} ریال
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
              />
            }
            label="شرایط و قوانین را مطالعه کردم و می‌پذیرم"
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            پس از کلیک بر روی دکمه پرداخت، به درگاه بانکی منتقل خواهید شد
          </Alert>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
            <Button onClick={handleBackStep} disabled={processing}>
              بازگشت
            </Button>
            <Button
              variant="contained"
              onClick={handleNextStep}
              disabled={!acceptTerms || processing}
              startIcon={
                processing ? <CircularProgress size={20} /> : <PaymentIcon />
              }
            >
              {processing ? "در حال انتقال..." : "پرداخت"}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 2: Payment Result */}
      {activeStep === 2 && (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          {verifying ? (
            <Box sx={{ py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 3 }}>
                در حال تأیید پرداخت...
              </Typography>
            </Box>
          ) : paymentResult ? (
            <>
              {paymentResult.success ? (
                <>
                  <SuccessIcon
                    sx={{ fontSize: 80, color: "success.main", mb: 2 }}
                  />
                  <Typography variant="h5" gutterBottom color="success.main">
                    پرداخت موفق
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {paymentResult.message}
                  </Typography>

                  {paymentResult.referenceNumber && (
                    <Box
                      sx={{
                        bgcolor: "success.light",
                        p: 2,
                        borderRadius: 1,
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        شماره پیگیری:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {paymentResult.referenceNumber}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <Button variant="contained" onClick={handleBackToInvoice}>
                      بازگشت به فاکتورها
                    </Button>
                    {invoice.orderId && (
                      <Button variant="outlined" onClick={handleBackToOrder}>
                        مشاهده سفارش
                      </Button>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <ErrorIcon
                    sx={{ fontSize: 80, color: "error.main", mb: 2 }}
                  />
                  <Typography variant="h5" gutterBottom color="error.main">
                    پرداخت ناموفق
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {paymentResult.message}
                  </Typography>

                  <Alert severity="warning" sx={{ mb: 3 }}>
                    در صورت کسر وجه از حساب شما، مبلغ ظرف 72 ساعت به حساب شما
                    بازگردانده خواهد شد
                  </Alert>

                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep(1)}
                    >
                      تلاش مجدد
                    </Button>
                    <Button variant="outlined" onClick={handleBackToInvoice}>
                      بازگشت به فاکتورها
                    </Button>
                  </Box>
                </>
              )}
            </>
          ) : (
            <Box sx={{ py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 3 }}>
                در حال پردازش...
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default PaymentGateway;
